import { useRef } from 'react';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { AlertCircle, X, Loader2 } from 'lucide-react';
import './ConfirmModal.css';

interface ConfirmModalProps {
  open: boolean;
  title: string;
  message: string;
  detail?: string;
  confirmLabel?: string;
  confirmVariant?: 'danger' | 'primary';
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  open,
  title,
  message,
  detail,
  confirmLabel = 'Confirm',
  confirmVariant = 'danger',
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, open, onCancel);

  if (!open) return null;

  return (
    <div className="confirm-modal-overlay">
      <div
        ref={dialogRef}
        className="confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
      >
        <div className="confirm-modal-header">
          <h2 id="confirm-modal-title">{title}</h2>
          <button className="confirm-modal-close" onClick={onCancel} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="confirm-modal-body">
          <div className="confirm-modal-warning">
            <AlertCircle size={32} className="warning-icon" />
            <div className="warning-text">
              <p>{message}</p>
              {detail && <p className="warning-detail">{detail}</p>}
            </div>
          </div>
        </div>

        <div className="confirm-modal-footer">
          <button className="btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button className={`btn-confirm ${confirmVariant}`} onClick={onConfirm} disabled={loading}>
            {loading ? <Loader2 size={14} className="spin" /> : null}
            {loading ? 'Processing...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmModal;
